import { View, type ViewStyle } from "react-native";
import { type LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { MoonText } from "./MoonText";

/**
 * Label + value display row. Audit task 2.1.10.
 *
 * Canonicalises the GrantDetailScreen `InfoRow` shape:
 *   - optional leading icon (small, top-aligned with the label)
 *   - small uppercase faded label
 *   - regular weight value text below the label
 *
 * Display-only — for tappable rows with chevron navigation use
 * MoonSettingRow instead. The two primitives have a similar look but
 * different intents:
 *   - MoonSettingRow: tappable, navigational, label-left / value-right
 *     inline layout, chevron, optional toggle trailing.
 *   - MoonDetailRow: read-only display of a field in a detail view,
 *     label-above-value stacked layout.
 *
 * State coverage (Phase 1.0 matrix):
 *   - populated / empty / coming-soon — pass an `empty` placeholder
 *     value or render a coming-soon badge via the `trailing` slot.
 *   - error / focused / disabled: N/A; display-only.
 */
type Props = {
  /** Optional leading icon. */
  icon?: LucideIcon;
  /** Small uppercase faded label. */
  label: string;
  /** Value text or custom node (e.g. a badge). */
  value: string | React.ReactNode;
  /** Override the icon color. Defaults to `colors.textFaint`. */
  iconColor?: string;
  /** Override the value color. Defaults to `colors.textMid`. */
  valueColor?: string;
  /** Optional element rendered to the right of the value (e.g. a badge). */
  trailing?: React.ReactNode;
  testID?: string;
  style?: ViewStyle;
};

export function MoonDetailRow({
  icon: Icon,
  label,
  value,
  iconColor = colors.textFaint,
  valueColor = colors.textMid,
  trailing,
  testID,
  style,
}: Props) {
  return (
    <View
      testID={testID}
      style={[
        {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        },
        style,
      ]}
    >
      {Icon ? (
        <Icon
          size={14}
          color={iconColor}
          strokeWidth={1.5}
          style={{ marginTop: 2 }}
        />
      ) : null}
      <View style={{ flex: 1, minWidth: 0 }}>
        <MoonText
          style={{
            fontFamily: typography.families.sans,
            fontSize: 11,
            color: colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.3,
            marginBottom: 2,
          }}
        >
          {label}
        </MoonText>
        {typeof value === "string" ? (
          <MoonText
            style={{
              fontFamily: typography.families.sans,
              fontSize: 14,
              color: valueColor,
              lineHeight: 20,
            }}
          >
            {value}
          </MoonText>
        ) : (
          value
        )}
      </View>
      {trailing}
    </View>
  );
}
